import {NCRActionsDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import {DataGridActionTypes, DataGridColumnAlignments, DataGridColumnTypes, DataGridExoticColumnFields, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import Api from "../../../../../../../core/services/api/api";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import useSearch from "../../../../../../hooks/use-search";
import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {setReduxDialogState} from "../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import {Add, EditOutlined} from "@mui/icons-material";
import Button from "../../../../../../components/base/button/button";
import DateUtils from "../../../../../../../core/services/date-utils";
import notebookTags from "../../../../../../../core/models/static/routes/notebook-tags";
import {useParams} from "react-router-dom";
import Grid2 from "@mui/material/Unstable_Grid2";
import {IconButton, MenuItem, TextField, Typography} from "@mui/material";


const exportingChunkSize = 50;

const dataGridSaveConfig = {
    name: "NCRActionDataGrid",
    version: 1,
};

const orderByDictionary = {}

/**
 * NCR actions list action names
 */
const NCRListActionNames = {
    Edit: 'edit',
    Delete: 'delete'
}

const NCRInformationActionTable = forwardRef(({action, statuses, ncrCode, onTasksLoaded, onLoadingInitiated}, ref) => {
    const params = useParams();
    const [data, setData] = useState([]);
    const [changingTaskStatus, setChangingTaskStatus] = useState({});
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportNcrActionTable = operatorAccessLevels.packages.ncr.exportNcrActionTable;
    const dispatch = useDispatch();

    /**@type {React.MutableRefObject<DataGridController>}*/
    const dataGridControllerRef = useRef(null);

    /**
     * Memo version of column in the data grid
     * @type {DataGridColumn[]}
     */
    const actionListColumns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: DataGridExoticColumnFields.selection,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 100,
                resizable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                reorderable: false,
                sortable: true,
                pinned: true,
            },
            {
                name: NCRActionsDataGridCellKeys.edit,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                width: 0,
                resizable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                reorderable: false,
                sortable: true,
                pinned: true,
            },
            {
                title: "Title",
                name: NCRActionsDataGridCellKeys.title,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 50,
            },
            {
                title: "Action Owner",
                name: NCRActionsDataGridCellKeys.actionOwner,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 120,
            },
            {
                title: "Assignee",
                name: NCRActionsDataGridCellKeys.assignee,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 120,
            },
            {
                title: "Due Date",
                name: NCRActionsDataGridCellKeys.targetCompletionDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
            },
            {
                title: "Status",
                name: NCRActionsDataGridCellKeys.status,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
            },
            {
                title: "Description",
                name: NCRActionsDataGridCellKeys.description,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 200,
            },
        ], []);


    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: actionListColumns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Gets the list of actions by calling the api regarding this action
     * If the function is being called for export, the pagination info is passed as the input
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = {
            pagination: args.paginationInfo,
            keyword: args.filters.keyword,
            tags: notebookTags.ncrInformationActionGroup(params.reportId, action.id),
            ...(!!args.orderBy ? {
                sorts: [
                    Object.fromEntries(Object.entries(args.orderBy).map(([key, value]) =>
                            [
                                key === "descending" ? "isDesc" : "Property",
                                (value === true || value === false)
                                    ? value
                                    : orderByDictionary[value]
                            ]
                        )
                    )
                ]
            } : {})
        }
        onLoadingInitiated?.();
        const response = await Api.getPageTasksV2(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response?.data?.items ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0,
                currentPage: 1,
            }))
        }
        onTasksLoaded?.(response?.data?.totalCount ?? 0);
        if (response?.resultFlag && forExport)
            return response;
    }, [params.reportId, action?.id, isMounted, onTasksLoaded, onLoadingInitiated]);


    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize]);


    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(fetchEntries, initialSearchDataSettings, false)
    const {setLoading, setPaginationInfo} = searchData;

    /**
     * Resets the data and pagination info when the action changes
     */
    useEffect(() => {
        setLoading(true);
        setData([]);
        setPaginationInfo(initialPaginationInfo);
    }, [action?.id]);

    /**
     * Returns the object for exporting the list of drivers by preparing the title and body of the csv file
     * @param list
     * @return {{title: string, body: (*|*[])}}
     */
    const formatExportingData = useCallback((list) => {
        return {
            title: `Actions of ${action?.title} @ ${DateUtils.localNow().format(DateUtils.dateTimeFormat)}`,
            body: list?.map((e, index) => ({
                '': `${index + 1}`,
                'Title': e.title ?? '--',
                'Action Owner': (e?.createdByOperator?.firstname ?? "") + " " + (e?.createdByOperator?.lastname ?? ''),
                // 'Completion Date': e.completionDate ? DateUtils.serverToLocal(e.completionDate).format(DateUtils.dateFormat) : '--',
                'Due Date': e.dueDate ? DateUtils.serverToLocal(e.dueDate).format(DateUtils.dueDate) : '--',
                'Status': e.taskStatus?.title ?? '--',
                "Description": e.description ?? '--',
            })) ?? [],
        }
    }, [action]);

    /**
     * Exports the list of drivers in the system by calling the search function with the pagination info
     * Calls the function for formatting the data
     * @return {Promise<*>}
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res.data.items);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData])


    /**
     * Memo version of data grid footer actions
     * @type {DataGridFooterAction[]}
     */
    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]}*/
        [
            {
                name: NCRListActionNames.Edit,
                title: 'Edit',
                visibilityConstraints: {
                    minSelectedRows: 1,
                    maxSelectedRows: 1,
                },
            },
            {
                name: NCRListActionNames.Delete,
                title: 'Delete',
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                },
            },
        ], [])

    /**
     * Adds the received task to the list of tasks
     * @param task
     */
    const handleAddUpdateNewTask = useCallback((task) => {
        if (!isMounted())
            return;
        setData(prevState => {
            // if task exists, then merge otherwise add
            const index = prevState.findIndex(e => e.id === task.id);
            if (index === -1)
                return [task, ...prevState];
            return prevState.map(e => e.id === task.id ? ({...e, ...task}) : e)
        });
    }, [isMounted])

    /**
     * Exposes the add function to the parent component
     */
    useImperativeHandle(ref, () => ({
        addTask: (task) => handleAddUpdateNewTask(task),
    }), [handleAddUpdateNewTask])

    /**
     * Handles adding a new task to the list:
     * Dispatches opening the upsert task dialog with initial state
     */
    const onAddNewTaskClicked = useCallback(() => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertTask,
            {
                open: true,
                tags: notebookTags.ncrInformationActionGroup(params.reportId, action.id),
                onSuccess: handleAddUpdateNewTask,
                task: {
                    title: ncrCode?.toString(),
                },
                isNCRTask: false,
            }))
    }, [action.id, dispatch, handleAddUpdateNewTask, params.reportId, ncrCode])

    /**
     * Opens the upsert task dialog with the received task
     * @param task
     * @param close
     */
    const onEditTask = useCallback((task) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertTask,
            {
                open: true,
                onSuccess: handleAddUpdateNewTask,
                task: task,
                isNCRTask: false,
            }))
    }, [dispatch, handleAddUpdateNewTask]);


    /**
     * Opens the confirmation dialog regarding this action
     * If the process was confirmed, calls the remove
     */
    const getRemoveConfirmation = (itemsToBeRemoved) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {`Are you sure that you want to remove the following task(s)?`}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && _removeTasks(itemsToBeRemoved),
        );
    }

    /**
     * Calls the api regarding removing the list of actions form the list
     * @param actionIds list of drivers to be removed
     * @private
     */
    const _removeTasks = useCallback(async (actionIds) => {
        setLoading(true);
        for (const taskId of actionIds) {
            const response = await Api.removeTask(taskId);
            if (!isMounted())
                return
            if (response?.resultFlag) {
                setData(prevState => prevState.filter(e => !actionIds.includes(e.id)));
            }
        }
        if (!isMounted())
            return
        setLoading(false);
    }, [isMounted, setLoading]);

    /**
     * Fetches all the actions given the available length of the actions.
     * @param pageLength
     * @return {Promise<*[]>}
     */
    const getAllActions = async (pageLength) => {
        const _paginationInfo = {
            length: pageLength,
            pageSize: 20,
            currentPage: 1,
        };
        let entries = [];
        while (pageLength > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                continue;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res?.data?.items);
            if (!isMounted())
                return entries;
            _paginationInfo.currentPage++;
        }
        return entries;
    }

    /**
     * Handles the action over the selected row in the table based on the name of the action
     * @param action
     * @param selectedRows
     * @param excludedRows
     * @param allRowsSelected
     * @param pagination
     * @return {Promise<void>}
     */
    const onActionInvoked = async (action, selectedRows, excludedRows, allRowsSelected, pagination) => {
        dataGridControllerRef.current?.resetSelection();
        switch (action?.name) {
            case NCRListActionNames.Edit: {
                if (allRowsSelected && data.length === 1)
                    onEditTask(data[0]);
                else
                    onEditTask(data.find(e => e.id === (selectedRows[0].data)));
                break;
            }
            case NCRListActionNames.Delete: {
                if (!allRowsSelected) {
                    // not all rows are selected, therefore, the selectedRows list is not empty
                    return getRemoveConfirmation(selectedRows?.map(e => e.data));
                }
                if (pagination.pageSize >= pagination.length) {
                    // all the items are within a single page, so we have the data in hand
                    return getRemoveConfirmation(data?.map(e => e.id));
                }
                getRemoveConfirmation((await getAllActions(pagination.length))?.map(e => e.id));
                break;
            }
            default:
                break;
        }
    }

    /**
     * Changes the status of the task
     * @type {(function(*, *): Promise<void>)|*}
     */
    const changeTaskStatus = useCallback(async (task, statusId) => {
        setChangingTaskStatus(prevState => ({...prevState, [task.id]: true}));
        const response = await Api.changeTaskStatus({
            taskId: task.id,
            taskStatusId: statusId,
        })
        if (!isMounted())
            return;
        setChangingTaskStatus(prevState => ({...prevState, [task.id]: false}));
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === task.id ? ({...e, taskStatus: statuses.find(s => s.id === statusId)}) : e));
        }
    }, [isMounted, statuses])


    /**
     * Data grid rows entities
     * @type {unknown}
     */
    const dataGridRows = useMemo(() => {
        return data?.map(task => ({
            key: task.id,
            data: task.id,
            cells: {
                [DataGridExoticColumnFields.selection]: null,
                [NCRActionsDataGridCellKeys.title]: task.title,
                [NCRActionsDataGridCellKeys.edit]:
                    <IconButton
                        disabled={changingTaskStatus[task.id]}
                        size={'small'}
                        onClick={() => onEditTask(task)}
                        color={'paper.primary'}
                    >
                        <EditOutlined/>
                    </IconButton>
                ,
                [NCRActionsDataGridCellKeys.actionOwner]: (task?.createdByOperator?.firstname ?? "") + " " + (task?.createdByOperator?.lastname ?? ''),
                [NCRActionsDataGridCellKeys.assignee]: (task?.assignedToOperator?.firstname ?? "") + " " + (task?.assignedToOperator?.lastname ?? ''),
                [NCRActionsDataGridCellKeys.completionDate]: task?.completionDate,
                [NCRActionsDataGridCellKeys.targetCompletionDate]: task?.dueDate,
                [NCRActionsDataGridCellKeys.status]:
                    <TextField
                        select
                        fullWidth
                        variant={'outlined'}
                        size={'small'}
                        value={task?.taskStatus?.id}
                        onChange={(e) => changeTaskStatus(task, e.target.value)}
                        disabled={changingTaskStatus[task.id]}
                    >
                        {
                            statuses.map(status => (
                                <MenuItem key={status.id} value={status.id}>
                                    {status.title}
                                </MenuItem>
                            ))
                        }
                    </TextField>,
                [NCRActionsDataGridCellKeys.description]: task?.description,
            },
        }))
    }, [data, changingTaskStatus, statuses, onEditTask, changeTaskStatus]);


    return (
        <>
            <Grid2 container className={"pb-3"} spacing={2} margin={0}>
                <Grid2 xs>
                    <Typography variant={'body1'} fontWeight={600}>
                        {action.title ?? ""}
                    </Typography>
                </Grid2>
                <Grid2 xs={'auto'} className='d-flex justify-content-end'>
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        onClick={onAddNewTaskClicked}
                    >
                        <Add className='icon small mr-2'/>
                        <p className={'button-label'}>Add New Task</p>
                    </Button>
                </Grid2>
            </Grid2>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: actionListColumns,
                }}
                ref={dataGridControllerRef}
                columns={dataGridColumns}
                rows={dataGridRows}
                footerActions={footerActions}
                hideFooterActions={data?.length < 1}
                onActionInvoked={onActionInvoked}
                minimizeToolbar
                state={{
                    sortBy: searchData.orderBy,
                    pagination: searchData.paginationInfo,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                getExportInfo={canExportNcrActionTable ? exportToCsv : undefined}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
        </>
    )
});


export default NCRInformationActionTable;
